import { z } from 'zod';
import { Id, IdempotencyKey, IsoDateTime, JsonObject, TenantId } from './common.ts';
import { PolicyOrigin } from './policy.ts';

/**
 * Added by 21 step 0 (brief 21 §1, §1b.5; ADR-021): notifications are sent by an **external service** behind the
 * `Notifier` port (`notify-mock` by default, `notify-http` opt-in). The platform passes a template key and
 * parameters through; the external service renders and delivers. No rendering, SMTP or SMS in the platform.
 *
 * Every send is classified `external_communication` and goes through policy like any other effect. Exemptions are
 * policy, not transport: `platform-base` allows `origin: 'platform'` sends whose `templateKey` is in
 * `notifications.approvalExempt` (ships with `PLATFORM_NOTIFICATION_TEMPLATES`; a tenant may narrow it). `origin` is
 * derived from the trusted execution context, never from the request.
 */

export const NotificationChannel = z
  .enum(['email', 'sms', 'inapp', 'any'])
  .meta({ id: 'NotificationChannel' });
export type NotificationChannel = z.infer<typeof NotificationChannel>;

/** Who sends: platform core or a pack module (derived from the execution context, §1b.5); `PolicyOrigin`. */
export const NotificationOrigin = PolicyOrigin;
export type NotificationOrigin = PolicyOrigin;

/** Recipient: an internal principal, an external principal (invited party), or a raw address. */
export const NotificationRecipient = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('principal'), principalId: z.string().min(1) }),
    // 21b: an `ExternalPrincipal.id` (`ext-<uuid>`, any non-empty string), widened from `Id`.
    z.object({ kind: z.literal('external'), externalId: z.string().min(1) }),
    z.object({ kind: z.literal('address'), address: z.string().min(1).max(500) }),
  ])
  .meta({ id: 'NotificationRecipient' });
export type NotificationRecipient = z.infer<typeof NotificationRecipient>;

/** Pass-through request: the external service resolves the template and renders `params`. */
export const NotificationRequest = z
  .object({
    tenantId: TenantId,
    to: NotificationRecipient,
    templateKey: z.string().min(1).max(200),
    params: JsonObject.default({}),
    channel: NotificationChannel.default('any'),
    /** Idempotent by key: a second send with the same key returns the first receipt. */
    idempotencyKey: IdempotencyKey,
    caseId: Id.optional(),
    processInstanceId: Id.optional(),
    activationId: z.string().min(1).optional(),
  })
  .meta({ id: 'NotificationRequest' });
export type NotificationRequest = z.infer<typeof NotificationRequest>;
export type NotificationRequestInput = z.input<typeof NotificationRequest>;

export const NotificationStatus = z
  .enum(['accepted', 'delivered', 'failed', 'unknown'])
  .meta({ id: 'NotificationStatus' });
export type NotificationStatus = z.infer<typeof NotificationStatus>;

/** Ledgered receipt of one send; `unknown` is reconciled by the ledger sweep. */
export const NotificationReceipt = z
  .object({
    notificationId: Id,
    idempotencyKey: IdempotencyKey,
    status: NotificationStatus,
    channel: NotificationChannel,
    templateKey: z.string().min(1).max(200),
    /** Id the external service assigned (for reconciliation). */
    externalId: z.string().min(1).optional(),
    /** Adapter that sent it (`mock`, `http`). */
    adapter: z.string().min(1),
    /** Ledger action id of the governed send. */
    actionId: Id.optional(),
    reason: z.string().optional(),
    at: IsoDateTime,
  })
  .meta({ id: 'NotificationReceipt' });
export type NotificationReceipt = z.infer<typeof NotificationReceipt>;

/** Platform template keys (packs use their own `<module>.<name>` keys). The default `approvalExempt` list. */
export const PLATFORM_NOTIFICATION_TEMPLATES = {
  workItemAssigned: 'platform.work-item.assigned',
  workItemOverdue: 'platform.work-item.overdue',
  approvalRequested: 'platform.approval.requested',
  approvalEscalated: 'platform.approval.escalated',
  /**
   * Invitation of an external principal. Params: `token`, `link` (`<workbenchBaseUrl>/external#token=<token>`, the token
   * in the URL fragment, never a query string: `externalInvitationLink`), `expiresAt`, `caseIds`, `displayName`, `email`.
   */
  externalInvited: 'platform.external.invited',
  /** Added by 26a (brief 26 §6.9): ask an internal respondent to answer an obligation. Approval-exempt. */
  obligationAsked: 'platform.obligation.asked',
  /**
   * Added by 26a: ask an external respondent (also its reminder, with param `reminder: true`). **Not** approval-exempt:
   * an external ask is `external_communication` under policy (`PLATFORM_NOTIFICATION_APPROVAL_REQUIRED`).
   */
  obligationAskedExternal: 'platform.obligation.asked-external',
  /** Added by 26a: reminder before an obligation's deadline (internal respondent and owner). Approval-exempt. */
  obligationReminder: 'platform.obligation.reminder',
  /** Added by 26a: an overdue obligation escalated (escalation role, owner). Approval-exempt. */
  obligationEscalated: 'platform.obligation.escalated',
  /** Added by 26a: the ask could not be delivered (owner, in-app). Approval-exempt. */
  obligationUndeliverable: 'platform.obligation.undeliverable',
  /** Added by 26a: the case reached its outcome (creator, every answerer). Approval-exempt. */
  caseResolved: 'platform.case.resolved',
} as const;

/** Added by 26a: platform template keys that stay under the "external communication needs approval" policy. */
export const PLATFORM_NOTIFICATION_APPROVAL_REQUIRED: readonly string[] = [
  PLATFORM_NOTIFICATION_TEMPLATES.obligationAskedExternal,
];

/**
 * The default `notifications.approvalExempt` list: every platform template key except
 * `PLATFORM_NOTIFICATION_APPROVAL_REQUIRED` (26a: the external obligation ask).
 */
export const PLATFORM_NOTIFICATION_TEMPLATE_KEYS: readonly string[] = Object.values(
  PLATFORM_NOTIFICATION_TEMPLATES,
).filter((key) => !PLATFORM_NOTIFICATION_APPROVAL_REQUIRED.includes(key));

/** Profile section `notifications` (21b wires it). */
export const NotificationsConfig = z
  .object({
    adapter: z.enum(['mock', 'http']).default('mock'),
    /** `http`: endpoint of the external notification service (signed POST). */
    endpoint: z.url().optional(),
    /** Secret reference of the signing key (`http`). Never a secret value. */
    signingKeyRef: z.string().min(1).optional(),
    /** Platform template keys exempt from approval (policy rule in `platform-base`). */
    approvalExempt: z.array(z.string().min(1)).default([...PLATFORM_NOTIFICATION_TEMPLATE_KEYS]),
  })
  .meta({ id: 'NotificationsConfig' });
export type NotificationsConfig = z.infer<typeof NotificationsConfig>;

export const NOTIFICATION_EVENT_TYPES = {
  sent: 'notification.sent',
  failed: 'notification.failed',
} as const;

/** `notification.sent` / `notification.failed`. */
export const NotificationEventData = z
  .object({
    tenantId: TenantId,
    receipt: NotificationReceipt,
    to: NotificationRecipient,
    origin: NotificationOrigin,
    caseId: Id.optional(),
    commandId: z.string().min(1),
  })
  .meta({ id: 'NotificationEventData' });
export type NotificationEventData = z.infer<typeof NotificationEventData>;
